import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
	  #onQuantityChange(event) {
	    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

	    const { quantity, cartLine: line } = event.detail;
	    const tierSelector =
	      event.target instanceof HTMLElement ? event.target.closest('[data-roam-tier-selector="true"]') : null;

	    // Cart items require a line number
	    if (!line) return;

	    if (tierSelector instanceof HTMLElement) {
	      const currentPairCount = Number(tierSelector.dataset.roamTierPairCount || 0);
	      const displayQuantity = Number(tierSelector.dataset.roamTierDisplayQuantity || currentPairCount || 1);
	      const quantityDelta = Number(quantity) - displayQuantity;
	      const targetPairCount = Math.max(1, currentPairCount + quantityDelta);

	      if (targetPairCount === currentPairCount) {
	        const input = tierSelector.querySelector('input');
	        // The editable row shows paid pairs only; currentPairCount includes paid + free pairs.
	        if (input instanceof HTMLInputElement) input.value = String(displayQuantity);
	        return;
	      }

	      /** @type {Record<string, string | number>} */
	      let tierVariants = {};
	      /** @type {Record<string, string>} */
	      let tierProperties = {};
	      try {
	        tierVariants = JSON.parse(tierSelector.dataset.roamTierVariants || '{}');
	        tierProperties = JSON.parse(tierSelector.dataset.roamTierProperties || '{}');
	      } catch (error) {
	        console.error('Invalid bundle tier data', error);
	      }

	      const targetVariantId = tierVariants[String(targetPairCount)] || tierSelector.dataset.variantId;
	      if (!targetVariantId) return;

	      this.#changeRoamTierLine({
	        line,
	        lineKey: tierSelector.dataset.roamTierLineKey,
	        currentVariantId: tierSelector.dataset.variantId,
	        properties: tierProperties,
	        targetPairCount,
	        targetVariantId: String(targetVariantId),
	      });
	      return;
	    }

	    if (quantity === 0) {
	      return this.onLineItemRemove(line);
	    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Returns all cart item section ids that should be refreshed together.
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  /**
   * Switches a compression-socks tier line to the matching Shopify tier variant.
   * The visual quantity represents pairs; the real cart keeps one tier SKU line.
   * @param {{line: number, lineKey?: string, currentVariantId?: string, properties?: Record<string, string>, targetPairCount: number, targetVariantId: string}} config
   */
  #changeRoamTierLine(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('change-tier:user-action');
    const { line, lineKey, currentVariantId, properties: existingProperties = {}, targetPairCount, targetVariantId } = config;
    const { cartTotal } = this.refs;
    const sectionsToUpdate = this.#getSectionsToUpdate();
    const properties = {
      ...existingProperties,
      _roam_bundle: 'true',
      _roam_bundle_id: String(targetPairCount),
      _roam_bundle_quantity: String(targetPairCount),
    };
	// _roam_bundle_paid_units is gone with the rest: it existed to tell the cart
	// how many pairs to charge for, which is Shopify's job via the discount
	delete properties._roam_bundle_paid_units;
	delete properties._roam_bundle_price;
	delete properties._roam_bundle_price_cents;
	delete properties._roam_bundle_atc_price_cents;
	delete properties._roam_bundle_compare;
	delete properties._roam_bundle_compare_cents;
	delete properties._roam_bundle_per_pair;
	delete properties._roam_bundle_line_price_cents;
	delete properties._roam_bundle_discount_cents;
    const addBody = JSON.stringify({
      id: Number(targetVariantId),
      quantity: 1,
      properties,
    });
    const removePayload = /** @type {any} */ ({
      quantity: 0,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    if (lineKey) {
      removePayload.id = lineKey;
    } else {
      removePayload.line = line;
    }

    const removeBody = JSON.stringify(removePayload);
    const usesCurrentVariant = String(currentVariantId || '') === String(targetVariantId);

    this.#disableCartItems();
    cartTotal?.shimmer();

    let tierRequest;
    if (usesCurrentVariant) {
      const changePayload = /** @type {any} */ ({
        // the real pair count, not a discounted one — the automatic Buy X Get Y
        // discount is what takes the money off, so the line must carry every
        // pair the customer selected or Shopify has nothing to discount
        quantity: targetPairCount,
        properties,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
      if (lineKey) changePayload.id = lineKey;
      else changePayload.line = line;

      tierRequest = fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', { body: JSON.stringify(changePayload) })
      ).then((response) => {
        if (!response.ok) throw new Error(`Unable to update bundle tier (${response.status})`);
        return response.text();
      });
    } else {
      tierRequest = fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }))
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to add bundle tier (${response.status})`);
          return response.text();
        })
        .then((responseText) => {
          const parsedResponseText = JSON.parse(responseText);
          if (parsedResponseText.errors) throw new Error(parsedResponseText.errors);
          return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: removeBody }));
        })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to remove previous bundle tier (${response.status})`);
          return response.text();
        });
    }

    tierRequest
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

	    const sectionHtml = parsedResponseText.sections?.[this.sectionId];
	    if (!sectionHtml) {
	      sectionRenderer.renderSection(this.sectionId, { cache: false });
	      return;
	    }

        const newSectionHTML = new DOMParser().parseFromString(
	      sectionHtml,
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

	    morphSection(this.sectionId, sectionHtml);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
	  const selectors = document.querySelectorAll(
	    `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
	  );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
